import { type FC, useState, useEffect } from "react";
import {
  Table,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
  Spinner,
  TextInput,
  Breadcrumb,
  BreadcrumbItem,
  Badge,
  Card,
  Button,
  Textarea,
  Checkbox,
  Label,
} from "flowbite-react";
import { HiSearch, HiHome, HiRefresh, HiDownload } from "react-icons/hi";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import { supabase, TABLES } from "../../lib/supabase";
import { useNotification } from "../../contexts/NotificationContext";
import { resolveOrCreateAddressForRegeneration } from "../../services/addresses";
import {
  generateContractPdf,
  determinePdfTemplate,
} from "../../lib/pdf/contractPdfGenerator";
import { getWorkerData } from "../../utils/workerData";
import { downloadSignatureAsBase64 } from "../../utils/signatureStorage";
import type { ContractPdfData } from "../../types/pdf";

interface AddressSnapshot {
  id: number | null;
  street: string;
  house_number: string;
  zip_code: string;
  city: string;
  country: string;
}

interface ContractRow {
  contract_id: number;
  created_at: string;
  date_signed: string | null;
  signature_path: string | null;
  contract_type: string;
  contract_category: string;
  contract_company: string | null;
  contract_address: AddressSnapshot | null;
  customer: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    birthdate: string;
    bank_details: string;
    gender: number | null;
    address: AddressSnapshot;
  };
  worker: {
    full_name: string | null;
    user_id: string | null;
  };
}

interface UtilityDetails {
  previous_provider_name?: string;
  previous_customer_number?: string;
  annual_consumption_kwh?: number;
  meter_point_id?: string;
  meter_number?: string;
  meter_reading?: number;
  unit_price_ct_kwh?: number;
  base_price_eur_month?: number;
  delivery_start_date?: string;
  as_soon_as_possible?: boolean;
  desired_monthly_payment?: number;
}

// Gas/Strom detection aligned with contract creation flows
const isGasContract = (contractTypeName: string): boolean => {
  const lowerName = (contractTypeName || "").toLowerCase();
  if (lowerName.includes("erdgas")) return true;
  if (lowerName.includes("naturgas")) return true;
  return /\bgas\b/i.test(contractTypeName);
};

const isElectricityContract = (contractTypeName: string): boolean => {
  return (contractTypeName || "").toLowerCase().includes("strom");
};

const createEmptyUtilityDetails = (): UtilityDetails => ({
  previous_provider_name: "",
  previous_customer_number: "",
  annual_consumption_kwh: undefined,
  meter_point_id: "",
  meter_number: "",
  meter_reading: undefined,
  unit_price_ct_kwh: undefined,
  base_price_eur_month: undefined,
  delivery_start_date: "",
  as_soon_as_possible: false,
  desired_monthly_payment: undefined,
});

const toUtilityState = (payload: {
  previous_provider_name: string | null;
  previous_customer_number: string | null;
  annual_consumption_kwh: number | null;
  meter_point_id: string | null;
  meter_number: string | null;
  meter_reading: number | null;
  unit_price_ct_kwh: number | null;
  base_price_eur_month: number | null;
  delivery_start_date: string | null;
  as_soon_as_possible: boolean;
  desired_monthly_payment: number | null;
}): UtilityDetails => ({
  previous_provider_name: payload.previous_provider_name || undefined,
  previous_customer_number: payload.previous_customer_number || undefined,
  annual_consumption_kwh: payload.annual_consumption_kwh ?? undefined,
  meter_point_id: payload.meter_point_id || undefined,
  meter_number: payload.meter_number || undefined,
  meter_reading: payload.meter_reading ?? undefined,
  unit_price_ct_kwh: payload.unit_price_ct_kwh ?? undefined,
  base_price_eur_month: payload.base_price_eur_month ?? undefined,
  delivery_start_date: payload.delivery_start_date || undefined,
  as_soon_as_possible: payload.as_soon_as_possible,
  desired_monthly_payment: payload.desired_monthly_payment ?? undefined,
});

// Local helper to convert storage downloads to base64 data URLs
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const base64 = result.includes(",")
        ? result.split(",")[1] || result
        : result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const toAddressSnapshot = (address: any): AddressSnapshot => ({
  id: address?.id ? Number(address.id) : null,
  street: address?.street || "",
  house_number: address?.house_number || "",
  zip_code: address?.zip_code || "",
  city: address?.city || "",
  country: address?.country || "",
});

const getContractDisplayAddress = (contract: ContractRow): AddressSnapshot =>
  contract.contract_address ?? contract.customer.address;

const formatDateForPdfDisplay = (dateStr: string | null): string => {
  if (!dateStr) return "";
  const datePartMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (datePartMatch) {
    return `${datePartMatch[3]}.${datePartMatch[2]}.${datePartMatch[1]}`;
  }

  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const resolveContractPdfDate = (contract: ContractRow): string | null => {
  return (
    formatDateForPdfDisplay(contract.date_signed) ||
    formatDateForPdfDisplay(contract.created_at) ||
    null
  );
};

const PdfRegenerationPage: FC = () => {
  const { showNotification } = useNotification();
  const [contracts, setContracts] = useState<ContractRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const [selectedContract, setSelectedContract] = useState<ContractRow | null>(
    null
  );
  const [detailLoading, setDetailLoading] = useState(false);
  const [electricityDetails, setElectricityDetails] =
    useState<UtilityDetails | null>(null);
  const [gasDetails, setGasDetails] = useState<UtilityDetails | null>(null);
  const [electricityForm, setElectricityForm] = useState<UtilityDetails | null>(
    null
  );
  const [gasForm, setGasForm] = useState<UtilityDetails | null>(null);
  const [customerForm, setCustomerForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    birthdate: "",
    bank_details: "",
  });
  const [addressForm, setAddressForm] = useState({
    street: "",
    house_number: "",
    zip_code: "",
    city: "",
    country: "",
    address_id: null as number | null,
  });
  const [isSavingCustomer, setIsSavingCustomer] = useState(false);
  const [isDeletingCustomer, setIsDeletingCustomer] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    void fetchContracts(currentPage, searchTerm);
  }, [currentPage]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      // Always reset to page 1 when search changes
      setCurrentPage(1);
      void fetchContracts(1, searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchContracts = async (
    page: number,
    search: string
  ): Promise<ContractRow[]> => {
    setIsLoading(true);
    try {
      // Build the query
      // We need strict filtering for Stadtwerke & Gas/Strom
      // 'contract_type!inner' ensures we only get contracts that match the type criteria
      // 'customer!inner' is needed if we are searching customer fields

      let query = supabase
        .from(TABLES.CONTRACTS)
        .select(
          `
          id,
          created_at,
          date_signed,
          signature_path,
          contract_address:address_id (
            id,
            street,
            house_number,
            zip_code,
            city,
            country
          ),
          customer:customer_id!inner (
            id,
            first_name,
            last_name,
            email,
            phone_number,
            birthdate,
            bank_details,
            gender,
            address:address_id (
              id,
              street,
              house_number,
              zip_code,
              city,
              country
            )
          ),
          contract_type:contract_type_id!inner (
            type,
            category,
            company
          ),
          worker:worker_id (
            full_name,
            user_id
          )
        `,
          { count: "exact" }
        )
        .order("created_at", { ascending: false });

      // 1. Base Filter: Stadtwerke Dessau AND (Gas OR Strom)

      // Filter for 'Stadtwerke Dessau' in category or company
      query = query.or(
        "category.ilike.%stadtwerke dessau%,company.ilike.%stadtwerke dessau%",
        { foreignTable: "contract_type" }
      );

      // Filter for Gas or Strom (using ilike for broad matching similar to original regex)
      const gasStromFilter = [
        "type.ilike.%erdgas%",
        "type.ilike.%naturgas%",
        "type.ilike.%gas%",
        "type.ilike.%strom%",
      ].join(",");
      query = query.or(gasStromFilter, { foreignTable: "contract_type" });

      // 2. Search Logic
      if (search.trim()) {
        const term = search.trim();
        const isNumeric = !isNaN(Number(term));

        // If numeric, it might be an ID search, or just a number in a name/address
        if (isNumeric) {
          // Check ID OR customer fields
          query = query.or(
            `id.eq.${term},first_name.ilike.%${term}%,last_name.ilike.%${term}%,phone_number.ilike.%${term}%`,
            { foreignTable: "customer" } // CAUTION: mixing top-level ID and foreign table fields in one OR is tricky.
            // Supabase/PostgREST usually doesn't allow mixing tables in a single .or() string easily without raw PostgREST syntax.
            // Simplified approach for reliability:
            // If numeric, also search top-level ID specially?
            // Actually, `.or()` on the root table handles root columns.
            // Mixing root 'id' and customer fields requires careful syntax.
            // Let's split: exact ID match OR text search on customer.
          );
          // NOTE: The above combined OR with foreignTable might fail if 'id' is on root.
          // Correct approach for cross-table OR in Supabase is complex.
          // STRATEGY: Use the text search on customer fields mostly.
          // For ID, valid contracts are unlikely to match a name search perfectly if we don't mix them.
          // Let's optimize: standard text search on customer + contract_type.
        }

        const searchFilters = [
          `first_name.ilike.%${term}%`,
          `last_name.ilike.%${term}%`,
          `email.ilike.%${term}%`,
          // Add detailed address search if possible, but address is nested in customer.
          // Searching joined deeply nested (contracts -> customer -> address) is hard in one go.
          // We'll stick to customer name/email and contract type fields for now as per plan.
        ];

        // Also search contract_type fields
        // Note: We can't easily OR across 'customer' and 'contract_type' and 'root' in one generic clause.
        // We will prioritize CUSTOMER search as that is most important.

        query = query.or(searchFilters.join(","), { foreignTable: "customer" });
      }

      // 3. Pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      // Map the data (Client side filtering removed intentionally)
      const mapped: ContractRow[] = (data || []).map((item: any) => ({
        contract_id: item.id,
        created_at: item.created_at,
        date_signed: item.date_signed,
        signature_path: item.signature_path,
        contract_type: item.contract_type?.type || "",
        contract_category: item.contract_type?.category || "",
        contract_company: item.contract_type?.company || null,
        contract_address: item.contract_address
          ? toAddressSnapshot(item.contract_address)
          : null,
        customer: {
          id: item.customer.id,
          first_name: item.customer.first_name,
          last_name: item.customer.last_name,
          email: item.customer.email,
          phone_number: item.customer.phone_number,
          birthdate: item.customer.birthdate,
          bank_details: item.customer.bank_details,
          gender: item.customer.gender,
          address: toAddressSnapshot(item.customer.address),
        },
        worker: {
          full_name: item.worker?.full_name || null,
          user_id: item.worker?.user_id || null,
        },
      }));

      setContracts(mapped);
      if (count !== null) setTotalCount(count);
      return mapped;
    } catch (err) {
      console.error("Error fetching contracts:", err);
      showNotification("Fehler beim Laden der Verträge", "error");
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const loadUtilityDetails = async (contract: ContractRow) => {
    setDetailLoading(true);
    setElectricityDetails(null);
    setGasDetails(null);

    const wantsStrom = isElectricityContract(contract.contract_type);
    const wantsGas = isGasContract(contract.contract_type);

    try {
      if (wantsStrom) {
        const { data } = await supabase
          .from(TABLES.ELECTRICITY_DETAILS)
          .select("*")
          .eq("contract_id", contract.contract_id)
          .maybeSingle();
        if (data) {
          setElectricityDetails(data as UtilityDetails);
          setElectricityForm(data as UtilityDetails);
        } else {
          // Allow manual creation when the link is missing
          setElectricityDetails(null);
          setElectricityForm(createEmptyUtilityDetails());
        }
      } else {
        setElectricityDetails(null);
        setElectricityForm(null);
      }

      if (wantsGas) {
        const { data } = await supabase
          .from(TABLES.GAS_DETAILS)
          .select("*")
          .eq("contract_id", contract.contract_id)
          .maybeSingle();
        if (data) {
          setGasDetails(data as UtilityDetails);
          setGasForm(data as UtilityDetails);
        } else {
          // Allow manual creation when the link is missing
          setGasDetails(null);
          setGasForm(createEmptyUtilityDetails());
        }
      } else {
        setGasDetails(null);
        setGasForm(null);
      }
    } catch (err) {
      console.error("Error loading utility details:", err);
      showNotification("Details konnten nicht geladen werden", "error");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSelectContract = (contract: ContractRow) => {
    const selectedAddress = getContractDisplayAddress(contract);
    setSelectedContract(contract);
    setIsEditing(false);
    setCustomerForm({
      first_name: contract.customer.first_name || "",
      last_name: contract.customer.last_name || "",
      email: contract.customer.email || "",
      phone_number: contract.customer.phone_number || "",
      birthdate: contract.customer.birthdate || "",
      bank_details: contract.customer.bank_details || "",
    });
    setAddressForm({
      street: selectedAddress.street || "",
      house_number: selectedAddress.house_number || "",
      zip_code: selectedAddress.zip_code || "",
      city: selectedAddress.city || "",
      country: selectedAddress.country || "",
      address_id: selectedAddress.id,
    });
    void loadUtilityDetails(contract);
  };

  const handleRegeneratePdf = async (contract: ContractRow) => {
    setIsRegenerating(contract.contract_id);
    try {
      const contractAddress = getContractDisplayAddress(contract);
      let employeeSignatureBase64: string | undefined;
      let workerOrganization: string | undefined;
      let salesEmployeeName = contract.worker.full_name || undefined;
      if (contract.worker.user_id) {
        const workerData = await getWorkerData(contract.worker.user_id);
        if (workerData) {
          if (workerData.signature_base64) {
            employeeSignatureBase64 = workerData.signature_base64;
          }
          if (workerData.organization) {
            workerOrganization = workerData.organization;
          }
          if (!salesEmployeeName && workerData.full_name) {
            salesEmployeeName = workerData.full_name;
          }
        }
      }

      let customerSignatureBase64: string | undefined;
      if (contract.signature_path) {
        try {
          if (contract.signature_path.startsWith("data:")) {
            customerSignatureBase64 = contract.signature_path;
            console.log("Using inline customer signature (data URL)");
          } else {
            console.log(
              "Downloading customer signature from contract-signatures bucket:",
              contract.signature_path
            );
            const { data: signatureBlob, error: storageError } =
              await supabase.storage
                .from("contract-signatures")
                .download(contract.signature_path);

            if (storageError) {
              throw storageError;
            }

            if (signatureBlob) {
              const base64 = await blobToBase64(signatureBlob);
              customerSignatureBase64 = `data:image/png;base64,${base64}`;
              console.log("Customer signature loaded from storage");
            } else {
              throw new Error("Signature blob empty");
            }
          }
        } catch (err) {
          console.error("Error downloading customer signature:", err);
          // Fallback to public URL helper in case storage download fails (e.g. legacy public bucket)
          try {
            customerSignatureBase64 = await downloadSignatureAsBase64(
              contract.signature_path
            );
            console.log("Customer signature loaded via public URL fallback");
          } catch (fallbackErr) {
            console.error(
              "Fallback downloadSignatureAsBase64 also failed:",
              fallbackErr
            );
          }
        }
      }

      const wantsStrom = isElectricityContract(contract.contract_type);
      const wantsGas = isGasContract(contract.contract_type);

      // Prefer loaded details, but fall back to form values so regeneration works even before persisting
      let electricityDetailsData = electricityDetails || electricityForm;
      let gasDetailsData = gasDetails || gasForm;

      // If we still don't have details (e.g. direct regenerate), fetch them
      if (!electricityDetailsData && wantsStrom) {
        const { data } = await supabase
          .from(TABLES.ELECTRICITY_DETAILS)
          .select("*")
          .eq("contract_id", contract.contract_id)
          .maybeSingle();
        electricityDetailsData = data as UtilityDetails | null;
      }
      if (!gasDetailsData && wantsGas) {
        const { data } = await supabase
          .from(TABLES.GAS_DETAILS)
          .select("*")
          .eq("contract_id", contract.contract_id)
          .maybeSingle();
        gasDetailsData = data as UtilityDetails | null;
      }

      const toPdfUtility = (data?: UtilityDetails | null) => {
        if (!data) return undefined;
        return {
          previous_provider_name: data.previous_provider_name || "",
          previous_customer_number: data.previous_customer_number || "",
          annual_consumption_kwh:
            data.annual_consumption_kwh !== undefined &&
            data.annual_consumption_kwh !== null
              ? String(data.annual_consumption_kwh)
              : "",
          meter_point_id: data.meter_point_id || undefined,
          meter_number: data.meter_number || "",
          meter_reading:
            data.meter_reading !== undefined && data.meter_reading !== null
              ? String(data.meter_reading)
              : undefined,
          unit_price_ct_kwh:
            data.unit_price_ct_kwh !== undefined &&
            data.unit_price_ct_kwh !== null
              ? String(data.unit_price_ct_kwh)
              : "",
          base_price_eur_month:
            data.base_price_eur_month !== undefined &&
            data.base_price_eur_month !== null
              ? String(data.base_price_eur_month)
              : "",
          delivery_start_date: data.delivery_start_date || null,
          as_soon_as_possible: data.as_soon_as_possible ?? undefined,
          desired_monthly_payment:
            data.desired_monthly_payment !== undefined &&
            data.desired_monthly_payment !== null
              ? String(data.desired_monthly_payment)
              : undefined,
        };
      };

      const formatDate = (dateStr: string) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        return date.toLocaleDateString("de-DE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      };

      const selectedDate = resolveContractPdfDate(contract);
      if (!selectedDate) {
        showNotification(
          "Vertragsdatum konnte nicht ermittelt werden",
          "error"
        );
        return;
      }

      const pdfData: ContractPdfData = {
        customer: {
          firstName: contract.customer.first_name,
          lastName: contract.customer.last_name,
          birthdate: formatDate(contract.customer.birthdate),
          email: contract.customer.email,
          phone: contract.customer.phone_number,
          address: {
            street: contractAddress.street,
            houseNumber: contractAddress.house_number,
            zipCode: contractAddress.zip_code,
            city: contractAddress.city,
            country: contractAddress.country,
          },
        },
        gender: contract.customer.gender,
        bankDetails: contract.customer.bank_details,
        signatureBase64: customerSignatureBase64,
        contractType: contract.contract_type,
        selectedDate,
        employeeSignatureBase64: employeeSignatureBase64,
        salesEmployeeName,
        salesPartnerCompany: workerOrganization,
        signatureLocation: contractAddress.city,
        electricityDetails: toPdfUtility(electricityDetailsData),
        gasDetails: toPdfUtility(gasDetailsData),
      };

      const { templatePath } = determinePdfTemplate(contract.contract_type);
      const result = await generateContractPdf(pdfData, templatePath);

      if (result.success && result.pdfBlob) {
        const url = window.URL.createObjectURL(result.pdfBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Vertrag_${contract.customer.last_name}_${contract.contract_type}_REGENERATED.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showNotification("PDF erfolgreich regeneriert", "success");
      } else {
        throw new Error(result.error || "PDF generation failed");
      }
    } catch (error) {
      console.error("Error regenerating PDF:", error);
      showNotification("Fehler bei der PDF-Regenerierung", "error");
    } finally {
      setIsRegenerating(null);
    }
  };

  const handleUpdateCustomer = async () => {
    if (!selectedContract || !isEditing) return;

    const normalizedAddress = {
      street: addressForm.street.trim(),
      house_number: addressForm.house_number.trim(),
      zip_code: addressForm.zip_code.trim(),
      city: addressForm.city.trim(),
      country: addressForm.country.trim(),
    };

    if (
      !normalizedAddress.street ||
      !normalizedAddress.house_number ||
      !normalizedAddress.zip_code ||
      !normalizedAddress.city
    ) {
      showNotification(
        "Bitte Straße, Hausnummer, PLZ und Stadt vollständig eintragen",
        "error"
      );
      return;
    }

    setIsSavingCustomer(true);
    const toNumberOrNull = (value: any) =>
      value === undefined || value === null || value === ""
        ? null
        : Number(value);
    const wantsStrom = isElectricityContract(selectedContract.contract_type);
    const wantsGas = isGasContract(selectedContract.contract_type);
    try {
      try {
        const resolvedAddress = await resolveOrCreateAddressForRegeneration({
          ...normalizedAddress,
        });

        const currentContractAddressId = selectedContract.contract_address?.id;
        if (resolvedAddress.id !== currentContractAddressId) {
          const { error: contractAddressError } = await supabase
            .from(TABLES.CONTRACTS)
            .update({
              address_id: resolvedAddress.id,
            })
            .eq("id", selectedContract.contract_id);

          if (contractAddressError) {
            throw contractAddressError;
          }
        }
      } catch (addressError) {
        console.error("Error resolving/relinking address:", addressError);
        showNotification("Adresse konnte nicht gespeichert werden", "error");
        return;
      }

      const { error: customerError } = await supabase
        .from(TABLES.CUSTOMERS)
        .update({
          first_name: customerForm.first_name,
          last_name: customerForm.last_name,
          email: customerForm.email,
          phone_number: customerForm.phone_number,
          birthdate: customerForm.birthdate,
          bank_details: customerForm.bank_details,
        })
        .eq("id", selectedContract.customer.id);

      if (customerError) throw customerError;

      // Update electricity details when available
      if (wantsStrom && electricityForm) {
        const electricityPayload = {
          contract_id: selectedContract.contract_id,
          previous_provider_name:
            electricityForm.previous_provider_name || null,
          previous_customer_number:
            electricityForm.previous_customer_number || null,
          annual_consumption_kwh: toNumberOrNull(
            electricityForm.annual_consumption_kwh
          ),
          meter_point_id: electricityForm.meter_point_id || null,
          meter_number: electricityForm.meter_number || null,
          meter_reading: toNumberOrNull(electricityForm.meter_reading),
          unit_price_ct_kwh: toNumberOrNull(electricityForm.unit_price_ct_kwh),
          base_price_eur_month: toNumberOrNull(
            electricityForm.base_price_eur_month
          ),
          delivery_start_date: electricityForm.delivery_start_date || null,
          as_soon_as_possible: electricityForm.as_soon_as_possible ?? false,
          desired_monthly_payment: toNumberOrNull(
            electricityForm.desired_monthly_payment
          ),
        };

        if (electricityDetails) {
          const { error: elecError } = await supabase
            .from(TABLES.ELECTRICITY_DETAILS)
            .update({
              previous_provider_name: electricityPayload.previous_provider_name,
              previous_customer_number:
                electricityPayload.previous_customer_number,
              annual_consumption_kwh: electricityPayload.annual_consumption_kwh,
              meter_point_id: electricityPayload.meter_point_id,
              meter_number: electricityPayload.meter_number,
              meter_reading: electricityPayload.meter_reading,
              unit_price_ct_kwh: electricityPayload.unit_price_ct_kwh,
              base_price_eur_month: electricityPayload.base_price_eur_month,
              delivery_start_date: electricityPayload.delivery_start_date,
              as_soon_as_possible: electricityPayload.as_soon_as_possible,
              desired_monthly_payment:
                electricityPayload.desired_monthly_payment,
            })
            .eq("contract_id", selectedContract.contract_id);
          if (elecError) throw elecError;
        } else {
          const { error: elecInsertError } = await supabase
            .from(TABLES.ELECTRICITY_DETAILS)
            .upsert(electricityPayload, { onConflict: "contract_id" });
          if (elecInsertError) throw elecInsertError;
          setElectricityDetails(toUtilityState(electricityPayload));
        }
        setElectricityForm(toUtilityState(electricityPayload));
      }

      // Update gas details when available
      if (wantsGas && gasForm) {
        const gasPayload = {
          contract_id: selectedContract.contract_id,
          previous_provider_name: gasForm.previous_provider_name || null,
          previous_customer_number: gasForm.previous_customer_number || null,
          annual_consumption_kwh: toNumberOrNull(
            gasForm.annual_consumption_kwh
          ),
          meter_point_id: gasForm.meter_point_id || null,
          meter_number: gasForm.meter_number || null,
          meter_reading: toNumberOrNull(gasForm.meter_reading),
          unit_price_ct_kwh: toNumberOrNull(gasForm.unit_price_ct_kwh),
          base_price_eur_month: toNumberOrNull(gasForm.base_price_eur_month),
          delivery_start_date: gasForm.delivery_start_date || null,
          as_soon_as_possible: gasForm.as_soon_as_possible ?? false,
          desired_monthly_payment: toNumberOrNull(
            gasForm.desired_monthly_payment
          ),
        };

        if (gasDetails) {
          const { error: gasError } = await supabase
            .from(TABLES.GAS_DETAILS)
            .update({
              previous_provider_name: gasPayload.previous_provider_name,
              previous_customer_number: gasPayload.previous_customer_number,
              annual_consumption_kwh: gasPayload.annual_consumption_kwh,
              meter_point_id: gasPayload.meter_point_id,
              meter_number: gasPayload.meter_number,
              meter_reading: gasPayload.meter_reading,
              unit_price_ct_kwh: gasPayload.unit_price_ct_kwh,
              base_price_eur_month: gasPayload.base_price_eur_month,
              delivery_start_date: gasPayload.delivery_start_date,
              as_soon_as_possible: gasPayload.as_soon_as_possible,
              desired_monthly_payment: gasPayload.desired_monthly_payment,
            })
            .eq("contract_id", selectedContract.contract_id);
          if (gasError) throw gasError;
        } else {
          const { error: gasInsertError } = await supabase
            .from(TABLES.GAS_DETAILS)
            .upsert(gasPayload, { onConflict: "contract_id" });
          if (gasInsertError) throw gasInsertError;
          setGasDetails(toUtilityState(gasPayload));
        }
        setGasForm(toUtilityState(gasPayload));
      }

      showNotification("Kunde aktualisiert", "success");
      const refreshedContracts = await fetchContracts(currentPage, searchTerm);
      // Re-select updated contract with fresh DB data
      const refreshed = refreshedContracts.find(
        (c) => c.contract_id === selectedContract.contract_id
      );
      if (refreshed) {
        handleSelectContract(refreshed);
      } else {
        setSelectedContract(null);
      }
      setIsEditing(false);
    } catch (err) {
      console.error("Error updating customer:", err);
      showNotification("Kunde konnte nicht aktualisiert werden", "error");
    } finally {
      setIsSavingCustomer(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!selectedContract) return;
    const confirmDelete = window.confirm(
      "Möchtest du diesen Kunden wirklich löschen? Alle verknüpften Verträge werden mit gelöscht."
    );
    if (!confirmDelete) return;
    setIsDeletingCustomer(true);
    try {
      const { error } = await supabase
        .from(TABLES.CUSTOMERS)
        .delete()
        .eq("id", selectedContract.customer.id);
      if (error) throw error;

      showNotification("Kunde gelöscht", "success");
      setContracts((prev) =>
        prev.filter((c) => c.customer.id !== selectedContract.customer.id)
      );
      setSelectedContract(null);
      setElectricityDetails(null);
      setGasDetails(null);
    } catch (err) {
      console.error("Error deleting customer:", err);
      showNotification("Kunde konnte nicht gelöscht werden", "error");
    } finally {
      setIsDeletingCustomer(false);
    }
  };

  return (
    <NavbarSidebarLayout pageTitle="PDF Regenerierung">
      <div className="p-4 lg:p-6">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-2">
            <Breadcrumb aria-label="Breadcrumb">
              <BreadcrumbItem href="/" icon={HiHome}>
                Dashboard
              </BreadcrumbItem>
              <BreadcrumbItem>PDF Regenerierung</BreadcrumbItem>
            </Breadcrumb>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                PDF Regenerierung (Stadtwerke Dessau)
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Nutze die Stadtwerke-Verträge (Strom/Gas), sehe Details und
                regeneriere PDFs.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              color="gray"
              onClick={() => fetchContracts(currentPage, searchTerm)}
              disabled={isLoading}
            >
              <HiRefresh className="mr-2 h-5 w-5" />
              Aktualisieren
            </Button>
          </div>
        </div>

        <Card className="mb-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="w-full md:w-96">
              <TextInput
                icon={HiSearch}
                placeholder="Suche nach Name, Vertragstyp oder E-Mail"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
              />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Gefundene Verträge: {totalCount}
            </p>
          </div>
        </Card>

        <div className="flex flex-col gap-6 lg:flex-row">
          <div className="w-full lg:w-2/3">
            <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
              <div className="overflow-x-auto">
                <Table
                  striped
                  className="dark:bg-gray-800 dark:text-gray-400"
                  theme={{
                    root: {
                      shadow: "hidden",
                      wrapper:
                        "relative overflow-x-auto shadow-md sm:rounded-lg",
                    },
                  }}
                >
                  <TableHead>
                    <TableHeadCell>Name</TableHeadCell>
                    <TableHeadCell>Kontakt</TableHeadCell>
                    <TableHeadCell>Adresse</TableHeadCell>
                    <TableHeadCell>Vertrag</TableHeadCell>
                    <TableHeadCell>Berater</TableHeadCell>
                    <TableHeadCell>Datum</TableHeadCell>
                    <TableHeadCell>Aktionen</TableHeadCell>
                  </TableHead>
                  <TableBody className="divide-y">
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <div className="flex justify-center py-6">
                            <Spinner />
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : contracts.length > 0 ? (
                      contracts.map((contract) => {
                        const displayAddress =
                          getContractDisplayAddress(contract);
                        return (
                          <TableRow
                            key={contract.contract_id}
                            className="bg-white text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
                          >
                            <TableCell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                              {contract.customer.first_name}{" "}
                              {contract.customer.last_name}
                            </TableCell>
                            <TableCell>
                              <div>
                                <p>{contract.customer.email}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {contract.customer.phone_number}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p>
                                  {displayAddress.street}{" "}
                                  {displayAddress.house_number}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {displayAddress.zip_code}{" "}
                                  {displayAddress.city}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge color="info" className="w-fit">
                                {contract.contract_type}
                              </Badge>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {contract.contract_company}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                #{contract.contract_id}
                              </div>
                            </TableCell>
                            <TableCell>
                              <p className="text-sm text-gray-900 dark:text-white">
                                {contract.worker.full_name || "—"}
                              </p>
                            </TableCell>
                            <TableCell>
                              {new Date(contract.created_at).toLocaleDateString(
                                "de-DE"
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-2">
                                <Button
                                  size="xs"
                                  color="gray"
                                  onClick={() => handleSelectContract(contract)}
                                >
                                  Details
                                </Button>
                                <Button
                                  size="xs"
                                  color="success"
                                  disabled={
                                    isRegenerating === contract.contract_id
                                  }
                                  onClick={() => handleRegeneratePdf(contract)}
                                >
                                  {isRegenerating === contract.contract_id ? (
                                    <span className="flex items-center gap-2">
                                      <Spinner size="sm" light />
                                      Wird regeneriert...
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-2">
                                      <HiDownload className="h-4 w-4" />
                                      PDF
                                    </span>
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center">
                          Keine Verträge gefunden.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Zeige{" "}
                  <span className="font-medium">
                    {Math.min((currentPage - 1) * pageSize + 1, totalCount)}
                  </span>{" "}
                  bis{" "}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, totalCount)}
                  </span>{" "}
                  von <span className="font-medium">{totalCount}</span>{" "}
                  Ergebnissen
                </div>
                <div className="flex gap-2">
                  <Button
                    color="light"
                    size="xs"
                    disabled={currentPage === 1}
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                  >
                    Vorherige
                  </Button>
                  <Button
                    color="light"
                    size="xs"
                    disabled={currentPage * pageSize >= totalCount}
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                  >
                    Nächste
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-1/3">
            <Card>
              <div className="mb-4 space-y-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Detailansicht
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Gas/Strom-spezifische Daten und PDF-Action
                  </p>
                </div>
                {selectedContract && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      color="primary"
                      size="xs"
                      onClick={() => setIsEditing((prev) => !prev)}
                      className="shadow-sm"
                    >
                      {isEditing ? "Bearbeiten beenden" : "Bearbeiten"}
                    </Button>
                    <Button
                      color="light"
                      size="xs"
                      onClick={() =>
                        selectedContract &&
                        handleSelectContract(selectedContract)
                      }
                      disabled={!isEditing}
                    >
                      Änderungen verwerfen
                    </Button>
                    <Button
                      color="success"
                      size="xs"
                      onClick={() => handleRegeneratePdf(selectedContract)}
                      disabled={isRegenerating === selectedContract.contract_id}
                    >
                      {isRegenerating === selectedContract.contract_id ? (
                        <span className="flex items-center gap-2">
                          <Spinner size="sm" light />
                          Regeneriere...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <HiDownload className="h-4 w-4" />
                          PDF regenerieren
                        </span>
                      )}
                    </Button>
                  </div>
                )}
              </div>

              {!selectedContract ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Wähle einen Vertrag aus der Liste, um Details zu sehen.
                </p>
              ) : detailLoading ? (
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                  <Spinner size="sm" />
                  Lädt Details...
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Kunde
                    </h3>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <TextInput
                        value={customerForm.first_name}
                        onChange={(e) =>
                          setCustomerForm((prev) => ({
                            ...prev,
                            first_name: e.target.value,
                          }))
                        }
                        placeholder="Vorname"
                        disabled={!isEditing}
                      />
                      <TextInput
                        value={customerForm.last_name}
                        onChange={(e) =>
                          setCustomerForm((prev) => ({
                            ...prev,
                            last_name: e.target.value,
                          }))
                        }
                        placeholder="Nachname"
                        disabled={!isEditing}
                      />
                      <TextInput
                        value={customerForm.email}
                        onChange={(e) =>
                          setCustomerForm((prev) => ({
                            ...prev,
                            email: e.target.value,
                          }))
                        }
                        placeholder="E-Mail"
                        type="email"
                        disabled={!isEditing}
                      />
                      <TextInput
                        value={customerForm.phone_number}
                        onChange={(e) =>
                          setCustomerForm((prev) => ({
                            ...prev,
                            phone_number: e.target.value,
                          }))
                        }
                        placeholder="Telefon"
                        disabled={!isEditing}
                      />
                      <TextInput
                        value={customerForm.birthdate || ""}
                        onChange={(e) =>
                          setCustomerForm((prev) => ({
                            ...prev,
                            birthdate: e.target.value,
                          }))
                        }
                        placeholder="Geburtsdatum"
                        type="date"
                        disabled={!isEditing}
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 mt-3">
                      <TextInput
                        value={addressForm.street}
                        onChange={(e) =>
                          setAddressForm((prev) => ({
                            ...prev,
                            street: e.target.value,
                          }))
                        }
                        placeholder="Straße"
                        disabled={!isEditing}
                      />
                      <TextInput
                        value={addressForm.house_number}
                        onChange={(e) =>
                          setAddressForm((prev) => ({
                            ...prev,
                            house_number: e.target.value,
                          }))
                        }
                        placeholder="Hausnummer"
                        disabled={!isEditing}
                      />
                      <TextInput
                        value={addressForm.zip_code}
                        onChange={(e) =>
                          setAddressForm((prev) => ({
                            ...prev,
                            zip_code: e.target.value,
                          }))
                        }
                        placeholder="PLZ"
                        disabled={!isEditing}
                      />
                      <TextInput
                        value={addressForm.city}
                        onChange={(e) =>
                          setAddressForm((prev) => ({
                            ...prev,
                            city: e.target.value,
                          }))
                        }
                        placeholder="Stadt"
                        disabled={!isEditing}
                      />
                      <TextInput
                        value={addressForm.country}
                        onChange={(e) =>
                          setAddressForm((prev) => ({
                            ...prev,
                            country: e.target.value,
                          }))
                        }
                        placeholder="Land"
                        disabled={!isEditing}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        Vertrag
                      </h3>
                      <p className="text-sm text-gray-700 dark:text-gray-200">
                        {selectedContract.contract_type}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {selectedContract.contract_company || "—"}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        #{selectedContract.contract_id}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(
                          selectedContract.created_at
                        ).toLocaleDateString("de-DE")}
                      </p>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        Berater
                      </h3>
                      <p className="text-sm text-gray-700 dark:text-gray-200">
                        {selectedContract.worker.full_name || "—"}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Bankdaten
                    </h3>
                    <Textarea
                      rows={4}
                      value={customerForm.bank_details}
                      onChange={(e) =>
                        setCustomerForm((prev) => ({
                          ...prev,
                          bank_details: e.target.value,
                        }))
                      }
                      placeholder="Bankdaten"
                      disabled={!isEditing}
                    />
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      color="info"
                      size="sm"
                      onClick={handleUpdateCustomer}
                      disabled={isSavingCustomer || !isEditing}
                    >
                      {isSavingCustomer ? (
                        <span className="flex items-center gap-2">
                          <Spinner size="sm" light />
                          Speichern...
                        </span>
                      ) : (
                        "Kunde aktualisieren"
                      )}
                    </Button>
                    <Button
                      color="failure"
                      size="sm"
                      onClick={handleDeleteCustomer}
                      disabled={isDeletingCustomer}
                    >
                      {isDeletingCustomer ? (
                        <span className="flex items-center gap-2">
                          <Spinner size="sm" light />
                          Löschen...
                        </span>
                      ) : (
                        "Kunde löschen"
                      )}
                    </Button>
                  </div>

                  {electricityForm && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        Strom-Details
                      </h3>
                      {!electricityDetails && (
                        <p className="mb-2 text-xs text-amber-600 dark:text-amber-400">
                          Keine Strom-Details verknüpft. Bitte ausfüllen und
                          speichern, um einen Datensatz anzulegen.
                        </p>
                      )}
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <TextInput
                          value={electricityForm.previous_provider_name || ""}
                          onChange={(e) =>
                            setElectricityForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    previous_provider_name: e.target.value,
                                  }
                                : { previous_provider_name: e.target.value }
                            )
                          }
                          placeholder="Bisheriger Anbieter"
                          disabled={!isEditing}
                        />
                        <TextInput
                          value={electricityForm.previous_customer_number || ""}
                          onChange={(e) =>
                            setElectricityForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    previous_customer_number: e.target.value,
                                  }
                                : { previous_customer_number: e.target.value }
                            )
                          }
                          placeholder="Kundennummer"
                          disabled={!isEditing}
                        />
                        <TextInput
                          value={
                            electricityForm.annual_consumption_kwh?.toString() ||
                            ""
                          }
                          onChange={(e) =>
                            setElectricityForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    annual_consumption_kwh: e.target
                                      .value as any,
                                  }
                                : {
                                    annual_consumption_kwh: e.target
                                      .value as any,
                                  }
                            )
                          }
                          placeholder="Verbrauch (kWh)"
                          disabled={!isEditing}
                        />
                        <TextInput
                          value={electricityForm.meter_point_id || ""}
                          onChange={(e) =>
                            setElectricityForm((prev) =>
                              prev
                                ? { ...prev, meter_point_id: e.target.value }
                                : { meter_point_id: e.target.value }
                            )
                          }
                          placeholder="Zählpunkt"
                          disabled={!isEditing}
                        />
                        <TextInput
                          value={electricityForm.meter_number || ""}
                          onChange={(e) =>
                            setElectricityForm((prev) =>
                              prev
                                ? { ...prev, meter_number: e.target.value }
                                : { meter_number: e.target.value }
                            )
                          }
                          placeholder="Zählernummer"
                          disabled={!isEditing}
                        />
                        <TextInput
                          value={
                            electricityForm.meter_reading?.toString() || ""
                          }
                          onChange={(e) =>
                            setElectricityForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    meter_reading: e.target.value as any,
                                  }
                                : { meter_reading: e.target.value as any }
                            )
                          }
                          placeholder="Zählerstand"
                          disabled={!isEditing}
                        />
                        <TextInput
                          value={
                            electricityForm.unit_price_ct_kwh?.toString() || ""
                          }
                          onChange={(e) =>
                            setElectricityForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    unit_price_ct_kwh: e.target.value as any,
                                  }
                                : { unit_price_ct_kwh: e.target.value as any }
                            )
                          }
                          placeholder="Arbeitspreis ct/kWh"
                          disabled={!isEditing}
                        />
                        <TextInput
                          value={
                            electricityForm.base_price_eur_month?.toString() ||
                            ""
                          }
                          onChange={(e) =>
                            setElectricityForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    base_price_eur_month: e.target.value as any,
                                  }
                                : {
                                    base_price_eur_month: e.target.value as any,
                                  }
                            )
                          }
                          placeholder="Grundpreis €/Monat"
                          disabled={!isEditing}
                        />
                        <TextInput
                          value={electricityForm.delivery_start_date || ""}
                          onChange={(e) =>
                            setElectricityForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    delivery_start_date: e.target.value,
                                  }
                                : { delivery_start_date: e.target.value }
                            )
                          }
                          placeholder="Lieferstart"
                          type="date"
                          disabled={!isEditing}
                        />
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={
                              electricityForm.as_soon_as_possible ?? false
                            }
                            onChange={(e) =>
                              setElectricityForm((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      as_soon_as_possible: e.target.checked,
                                    }
                                  : { as_soon_as_possible: e.target.checked }
                              )
                            }
                            disabled={!isEditing}
                          />
                          <Label className="text-sm text-gray-700 dark:text-gray-200">
                            Schnellstmöglich
                          </Label>
                        </div>
                        <TextInput
                          value={
                            electricityForm.desired_monthly_payment?.toString() ||
                            ""
                          }
                          onChange={(e) =>
                            setElectricityForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    desired_monthly_payment: e.target
                                      .value as any,
                                  }
                                : {
                                    desired_monthly_payment: e.target
                                      .value as any,
                                  }
                            )
                          }
                          placeholder="Monatlicher Abschlag"
                          disabled={!isEditing}
                        />
                      </div>
                    </div>
                  )}

                  {gasForm && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        Gas-Details
                      </h3>
                      {!gasDetails && (
                        <p className="mb-2 text-xs text-amber-600 dark:text-amber-400">
                          Keine Gas-Details verknüpft. Bitte ausfüllen und
                          speichern, um einen Datensatz anzulegen.
                        </p>
                      )}
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <TextInput
                          value={gasForm.previous_provider_name || ""}
                          onChange={(e) =>
                            setGasForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    previous_provider_name: e.target.value,
                                  }
                                : { previous_provider_name: e.target.value }
                            )
                          }
                          placeholder="Bisheriger Anbieter"
                          disabled={!isEditing}
                        />
                        <TextInput
                          value={gasForm.previous_customer_number || ""}
                          onChange={(e) =>
                            setGasForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    previous_customer_number: e.target.value,
                                  }
                                : { previous_customer_number: e.target.value }
                            )
                          }
                          placeholder="Kundennummer"
                          disabled={!isEditing}
                        />
                        <TextInput
                          value={
                            gasForm.annual_consumption_kwh?.toString() || ""
                          }
                          onChange={(e) =>
                            setGasForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    annual_consumption_kwh: e.target
                                      .value as any,
                                  }
                                : {
                                    annual_consumption_kwh: e.target
                                      .value as any,
                                  }
                            )
                          }
                          placeholder="Verbrauch (kWh)"
                          disabled={!isEditing}
                        />
                        <TextInput
                          value={gasForm.meter_point_id || ""}
                          onChange={(e) =>
                            setGasForm((prev) =>
                              prev
                                ? { ...prev, meter_point_id: e.target.value }
                                : { meter_point_id: e.target.value }
                            )
                          }
                          placeholder="Zählpunkt"
                          disabled={!isEditing}
                        />
                        <TextInput
                          value={gasForm.meter_number || ""}
                          onChange={(e) =>
                            setGasForm((prev) =>
                              prev
                                ? { ...prev, meter_number: e.target.value }
                                : { meter_number: e.target.value }
                            )
                          }
                          placeholder="Zählernummer"
                          disabled={!isEditing}
                        />
                        <TextInput
                          value={gasForm.meter_reading?.toString() || ""}
                          onChange={(e) =>
                            setGasForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    meter_reading: e.target.value as any,
                                  }
                                : { meter_reading: e.target.value as any }
                            )
                          }
                          placeholder="Zählerstand"
                          disabled={!isEditing}
                        />
                        <TextInput
                          value={gasForm.unit_price_ct_kwh?.toString() || ""}
                          onChange={(e) =>
                            setGasForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    unit_price_ct_kwh: e.target.value as any,
                                  }
                                : { unit_price_ct_kwh: e.target.value as any }
                            )
                          }
                          placeholder="Arbeitspreis ct/kWh"
                          disabled={!isEditing}
                        />
                        <TextInput
                          value={gasForm.base_price_eur_month?.toString() || ""}
                          onChange={(e) =>
                            setGasForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    base_price_eur_month: e.target.value as any,
                                  }
                                : {
                                    base_price_eur_month: e.target.value as any,
                                  }
                            )
                          }
                          placeholder="Grundpreis €/Monat"
                          disabled={!isEditing}
                        />
                        <TextInput
                          value={gasForm.delivery_start_date || ""}
                          onChange={(e) =>
                            setGasForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    delivery_start_date: e.target.value,
                                  }
                                : { delivery_start_date: e.target.value }
                            )
                          }
                          placeholder="Lieferstart"
                          type="date"
                          disabled={!isEditing}
                        />
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={gasForm.as_soon_as_possible ?? false}
                            onChange={(e) =>
                              setGasForm((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      as_soon_as_possible: e.target.checked,
                                    }
                                  : { as_soon_as_possible: e.target.checked }
                              )
                            }
                            disabled={!isEditing}
                          />
                          <Label className="text-sm text-gray-700 dark:text-gray-200">
                            Schnellstmöglich
                          </Label>
                        </div>
                        <TextInput
                          value={
                            gasForm.desired_monthly_payment?.toString() || ""
                          }
                          onChange={(e) =>
                            setGasForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    desired_monthly_payment: e.target
                                      .value as any,
                                  }
                                : {
                                    desired_monthly_payment: e.target
                                      .value as any,
                                  }
                            )
                          }
                          placeholder="Monatlicher Abschlag"
                          disabled={!isEditing}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </NavbarSidebarLayout>
  );
};

export default PdfRegenerationPage;
